import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BookingModal from './components/BookingModal';
import { properties } from '@/mocks/properties';
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed';

const PropertyDetailPage = () => {
  const { id } = useParams();
  const property = properties.find(p => p.id === id);
  const [showBooking, setShowBooking] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [saved, setSaved] = useState(false);
  const { trackView } = useRecentlyViewed();

  useEffect(() => {
    if (id) trackView(id);
  }, [id, trackView]);

  useEffect(() => {
    if (!property) return;
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: property.title,
      description: property.description,
      image: property.images.length > 0 ? property.images : [property.image],
      url: `${siteUrl}/listings/${property.id}`,
      offers: {
        '@type': 'Offer',
        price: property.price,
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        priceValidUntil: '2027-01-01',
        url: `${siteUrl}/listings/${property.id}`,
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: property.rating,
        bestRating: 5,
        worstRating: 1,
        ratingCount: 24,
      },
      additionalProperty: [
        { '@type': 'PropertyValue', name: 'Bedrooms', value: property.bedrooms },
        { '@type': 'PropertyValue', name: 'Bathrooms', value: property.bathrooms },
        { '@type': 'PropertyValue', name: 'Size', value: `${property.sqft} sqft` },
        { '@type': 'PropertyValue', name: 'Year Built', value: property.yearBuilt },
        { '@type': 'PropertyValue', name: 'City', value: property.city },
        { '@type': 'PropertyValue', name: 'Listing Type', value: property.priceType === 'rent' ? 'For Rent' : 'For Sale' },
      ],
    };

    const schemaId = 'schema-property-detail';
    let el = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = schemaId;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);

    document.title = `${property.title} — ${property.city} | MyAbode`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `${property.title} in ${property.location}. ${property.bedrooms} bed, ${property.bathrooms} bath, ${property.sqft.toLocaleString()} sqft. ${property.priceType === 'rent' ? `$${property.price.toLocaleString()}/mo` : `$${property.price.toLocaleString()}`}. Book a viewing on MyAbode.`);

    return () => {
      const s = document.getElementById(schemaId);
      if (s) s.remove();
    };
  }, [property]);

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-stone-700 mb-2">Property not found</h2>
          <Link to="/listings" className="text-amber-500 hover:underline">Back to Listings</Link>
        </div>
      </div>
    );
  }

  const allImages = property.images.length > 0 ? property.images : [property.image];

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="pt-[72px]">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-2 text-xs text-stone-400">
            <Link to="/" className="hover:text-amber-500 cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/listings" className="hover:text-amber-500 cursor-pointer">Listings</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-stone-600 line-clamp-1">{property.title}</span>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left: Images + Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image Gallery */}
              <div className="rounded-2xl overflow-hidden bg-stone-200">
                <div className="w-full h-72 md:h-96">
                  <img
                    src={allImages[activeImg]}
                    alt={property.title}
                    title={`${property.title} — ${property.city}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {allImages.length > 1 && (
                  <div className="flex gap-2 p-3 bg-white">
                    {allImages.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImg(i)}
                        className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-colors cursor-pointer ${activeImg === i ? 'border-amber-500' : 'border-transparent'}`}
                      >
                        <img src={img} alt={`${property.title} view ${i + 1}`} className="w-full h-full object-cover object-top" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Title & Info */}
              <div className="bg-white rounded-2xl p-6 border border-stone-100">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex gap-2 mb-2">
                      {property.isNew && <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">New</span>}
                      <span className="px-2.5 py-0.5 bg-stone-100 text-stone-600 text-xs font-semibold rounded-full">{property.type}</span>
                      <span className="px-2.5 py-0.5 bg-stone-100 text-stone-600 text-xs font-semibold rounded-full capitalize">{property.priceType === 'rent' ? 'For Rent' : 'For Sale'}</span>
                    </div>
                    <h1 className="text-2xl md:text-3xl font-black text-stone-900">{property.title}</h1>
                    <p className="text-stone-400 text-sm mt-1 flex items-center gap-1">
                      <i className="ri-map-pin-line text-amber-500"></i>
                      {property.location}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-black text-amber-600">
                      ${property.price.toLocaleString()}
                      {property.priceType === 'rent' && <span className="text-base font-normal text-stone-400">/mo</span>}
                    </p>
                    <div className="flex items-center gap-1 justify-end mt-1">
                      <i className="ri-star-fill text-amber-400 text-sm"></i>
                      <span className="font-semibold text-stone-700 text-sm">{property.rating}</span>
                    </div>
                  </div>
                </div>

                {/* Specs */}
                <div className="grid grid-cols-3 gap-4 py-4 border-y border-stone-100">
                  {[
                    { icon: 'ri-hotel-bed-line', value: `${property.bedrooms} Bedrooms` },
                    { icon: 'ri-drop-line', value: `${property.bathrooms} Bathrooms` },
                    { icon: 'ri-layout-line', value: `${property.sqft.toLocaleString()} sqft` },
                  ].map(spec => (
                    <div key={spec.value} className="text-center">
                      <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-xl mx-auto mb-1">
                        <i className={`${spec.icon} text-amber-500`}></i>
                      </div>
                      <p className="text-stone-600 text-xs font-medium">{spec.value}</p>
                    </div>
                  ))}
                </div>

                {/* Description */}
                <div className="mt-4">
                  <h3 className="font-bold text-stone-800 mb-2">About This Property</h3>
                  <p className="text-stone-500 text-sm leading-relaxed">{property.description}</p>
                </div>

                {/* Amenities */}
                <div className="mt-5">
                  <h3 className="font-bold text-stone-800 mb-3">Amenities</h3>
                  <div className="flex flex-wrap gap-2">
                    {property.amenities.map(a => (
                      <span key={a} className="px-3 py-1.5 bg-stone-50 border border-stone-200 text-stone-600 text-xs rounded-lg">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Booking Card */}
            <div className="space-y-5">
              {/* Agent Card */}
              <div className="bg-white rounded-2xl p-5 border border-stone-100">
                <h3 className="font-bold text-stone-800 mb-4 text-sm">Listed by</h3>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-stone-100">
                    <img src={property.agent.avatar} alt={property.agent.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-800 text-sm">{property.agent.name}</p>
                    <p className="text-stone-400 text-xs">Verified Agent</p>
                  </div>
                </div>
                <a
                  href={`tel:${property.agent.phone}`}
                  className="flex items-center gap-2 w-full py-2.5 border border-stone-200 rounded-xl text-stone-600 text-sm font-medium hover:border-amber-400 transition-colors cursor-pointer justify-center"
                >
                  <i className="ri-phone-line text-amber-500"></i>
                  {property.agent.phone}
                </a>
              </div>

              {/* Booking Card */}
              <div className="bg-white rounded-2xl p-5 border border-stone-100">
                <h3 className="font-bold text-stone-800 mb-2">Schedule a Viewing</h3>
                <p className="text-stone-400 text-xs mb-4">Pick a date and time that works for you</p>
                <button
                  onClick={() => setShowBooking(true)}
                  className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Book a Viewing
                </button>
                <button
                  onClick={() => setSaved(!saved)}
                  className={`w-full mt-3 py-3 border rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap ${saved ? 'border-red-200 text-red-500 bg-red-50' : 'border-stone-200 text-stone-600 hover:border-stone-300'}`}
                >
                  <i className={`${saved ? 'ri-heart-fill' : 'ri-heart-line'} mr-2`}></i>
                  {saved ? 'Saved' : 'Save Property'}
                </button>
              </div>

              {/* Quick Stats */}
              <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
                <div className="space-y-3">
                  {[
                    { label: 'Year Built', value: property.yearBuilt },
                    { label: 'Property Type', value: property.type },
                    { label: 'Listing Type', value: property.priceType === 'rent' ? 'For Rent' : 'For Sale' },
                    { label: 'City', value: property.city },
                  ].map(item => (
                    <div key={item.label} className="flex justify-between text-sm">
                      <span className="text-stone-500">{item.label}</span>
                      <span className="font-semibold text-stone-800">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showBooking && (
        <BookingModal property={property} onClose={() => setShowBooking(false)} />
      )}

      <Footer />
    </div>
  );
};

export default PropertyDetailPage;
